import store from '../state/store.js'
import {
  setAllRegistrations,
  setError,
  setOwnRegistration,
  setRegistrations,
  setResults,
  setSelectedRegistration,
  upsertRegistration
} from '../state/snacks'
import { setConnectionError } from '../state/error'
import { AuthorizedApi } from './index.js'

export function fetchRegistrations (config, auth) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(setRegistrations(response.data?.registrations ?? response.data))
    },
    (_error) => {
      store.dispatch(setError('Failed to load registrations'))
    }
  )
  api.fetchWithToken('voting/snacks/registrations', auth?.accessToken)
}

export function fetchAllRegistrations (config, auth) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(setAllRegistrations(response.data?.registrations ?? response.data))
    },
    (_error) => {
      store.dispatch(setError('Failed to load all registrations'))
    }
  )
  api.fetchWithToken('voting/snacks/registrations/all', auth?.accessToken)
}

export function fetchOwnRegistration (config, auth) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(setOwnRegistration(response.data))
    },
    (error) => {
      if (error?.response?.status === 404) {
        store.dispatch(setOwnRegistration(null))
        store.dispatch(setConnectionError({ error: false }))
      } else {
        store.dispatch(setError('Failed to load own registration'))
      }
    }
  )
  api.fetchWithToken('voting/snacks/registrations/me', auth?.accessToken)
}

export function fetchRegistrationByTableNumber (config, auth, tableNumber) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(setSelectedRegistration(response.data))
    },
    (error) => {
      if (error?.response?.status === 404) {
        store.dispatch(setError('Table not found'))
      } else {
        store.dispatch(setError('Failed to load registration'))
      }
    }
  )
  api.fetchWithToken(`voting/snacks/registrations/table/${tableNumber}`, auth?.accessToken)
}

export function registerTable (config, auth, data, onSuccess, onConflict, onError) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(upsertRegistration(response.data))
      store.dispatch(setOwnRegistration(response.data))
      if (onSuccess) onSuccess(response.data)
    },
    (error) => {
      if (error?.response?.status === 409) {
        if (onConflict) onConflict()
      } else if (onError) {
        onError(error)
      } else {
        store.dispatch(setError('Failed to register table'))
      }
    }
  )
  api.postWithToken('voting/snacks/registrations/register', auth?.accessToken, data)
}

export function fetchNextTableNumber (config, auth, onSuccess) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      if (onSuccess) onSuccess(response.data?.next_table_number)
    },
    (_error) => { /* silently ignore — table number field stays empty */ }
  )
  api.fetchWithToken('voting/snacks/registrations/next-table', auth?.accessToken)
}

export function createRegistration (config, auth, data, onSuccess, onConflict, onError) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(upsertRegistration(response.data))
      if (onSuccess) onSuccess(response.data)
    },
    (error) => {
      if (error?.response?.status === 409 && onConflict) {
        onConflict()
      } else if (onError) {
        onError(error)
      } else {
        store.dispatch(setError('Failed to create registration'))
      }
    }
  )
  api.postWithToken('voting/snacks/registrations', auth?.accessToken, data)
}

export function updateOwnRegistration (config, auth, data, onSuccess, onError) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(setOwnRegistration(response.data))
      store.dispatch(upsertRegistration(response.data))
      if (onSuccess) onSuccess(response.data)
    },
    (error) => {
      if (onError) {
        onError(error)
      } else {
        store.dispatch(setError('Failed to update registration'))
      }
    }
  )
  api.putWithToken('voting/snacks/registrations/me', auth?.accessToken, data)
}

export function updateRegistration (config, auth, regId, data, onSuccess, onError) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(upsertRegistration(response.data))
      if (onSuccess) onSuccess(response.data)
    },
    (error) => {
      if (onError) {
        onError(error)
      } else {
        store.dispatch(setError('Failed to update registration'))
      }
    }
  )
  api.putWithToken(`voting/snacks/registrations/${regId}`, auth?.accessToken, data)
}

export function activateRegistration (config, auth, regId, onSuccess, onConflict, onError) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(upsertRegistration(response.data))
      if (onSuccess) onSuccess(response.data)
    },
    (error) => {
      if (error?.response?.status === 409 && onConflict) {
        onConflict(error)
      } else if (onError) {
        onError(error)
      } else {
        store.dispatch(setError('Failed to activate registration'))
      }
    }
  )
  api.putWithToken(`voting/snacks/registrations/${regId}`, auth?.accessToken, { status: 'active' })
}

export function fetchSnacksResults (config, auth) {
  const api = new AuthorizedApi(
    config,
    (response) => {
      store.dispatch(setResults(response.data))
    },
    (_error) => {
      store.dispatch(setError('Failed to load results'))
    }
  )
  api.fetchWithToken('voting/snacks/results', auth?.accessToken)
}

export function deactivateRegistration (config, auth, regId, onSuccess) {
  const api = new AuthorizedApi(
    config,
    (_response) => {
      fetchAllRegistrations(config, auth)
      if (onSuccess) onSuccess()
    },
    (_error) => {
      store.dispatch(setError('Failed to deactivate registration'))
    }
  )
  api.deleteWithToken(`voting/snacks/registrations/${regId}`, auth?.accessToken)
}
